/**
 * E06-F01-T14 — "who is this research for": the recruiter, the recruiting team
 * lead and the sourcing team of the project a research request is linked to.
 *
 * Shown on the Intake form (once a project is picked) and on the Run screen.
 * Data: GET /research/projects/:id/people (useResearchProjectPeople). Read-only;
 * every value is React-escaped. A role nobody holds renders an explicit
 * "Not assigned" — never a silent blank.
 */
import type { JSX } from 'react';
import type { ResearchProjectPerson } from '@eer/shared';
import { useResearchProjectPeople } from './intakeApi.js';

export const PERSON_NOT_ASSIGNED = 'Not assigned';

function personText(p: ResearchProjectPerson | null | undefined): string {
  if (!p) return PERSON_NOT_ASSIGNED;
  return p.email && p.email !== p.name ? `${p.name} (${p.email})` : p.name;
}

export interface ResearchProjectPeopleProps {
  readonly projectId: number | null | undefined;
  /** data-testid prefix: `intake` → `intake-project-people`, `intake-project-recruiter`, … */
  readonly testIdPrefix?: string;
}

export function ResearchProjectPeople({
  projectId,
  testIdPrefix = 'intake',
}: ResearchProjectPeopleProps): JSX.Element | null {
  const query = useResearchProjectPeople(projectId);
  const tid = (suffix: string): string => `${testIdPrefix}-project-${suffix}`;
  if (typeof projectId !== 'number' || projectId <= 0) return null;

  if (query.isLoading) {
    return (
      <p className="text-xs text-gray-400" data-testid={tid('people-loading')}>
        Loading project people…
      </p>
    );
  }
  if (query.isError || !query.data) {
    return (
      <p className="text-xs text-red-600" data-testid={tid('people-error')}>
        Could not load the project recruiter and team leads.
      </p>
    );
  }

  const d = query.data;
  const sourcing = d.sourcingTeam;
  const rows: { key: string; label: string; value: string }[] = [
    { key: 'recruiter', label: 'Recruiter', value: personText(d.recruiter) },
    {
      key: 'recruiting-lead',
      label: 'Recruiting team lead',
      value: personText(d.recruitingTeamLead),
    },
    {
      key: 'sourcing-team',
      label: 'Sourcing team',
      value: sourcing
        ? sourcing.members.length > 0
          ? `${sourcing.name}: ${sourcing.members.map((m) => personText(m)).join(', ')}`
          : sourcing.name
        : PERSON_NOT_ASSIGNED,
    },
  ];

  return (
    <div
      className="border border-gray-200 rounded-lg bg-slate-50/60 px-4 py-3 mb-4 text-sm"
      data-testid={tid('people')}
      data-project-id={d.projectId}
    >
      <div className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
        <i className="fa-solid fa-user-tie text-[#D4AF37] mr-1" aria-hidden /> Project{' '}
        <span className="normal-case font-medium text-slate-700" data-testid={tid('label')}>
          {d.projectLabel}
        </span>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-1">
        {rows.map((r) => (
          <div key={r.key} className="flex flex-col" data-testid={tid(r.key)}>
            <dt className="text-[11px] uppercase tracking-wide text-gray-400">{r.label}</dt>
            <dd className="text-slate-700 font-medium break-words">{r.value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}
